import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ServiceRequestData } from '@services/types/Relationship/MyCalls/types';
import { Button, Input, Radio } from 'antd';
import { UnlockOutlined, LockOutlined, SendOutlined } from '@ant-design/icons';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import { useTranslation } from 'react-i18next';
import AttachmentSending from './AttachmentSending';
import * as S from './styles';

export type MessagesTypes = 'ALL' | 'ADMIN';

const { TextArea } = Input;

interface MessagesShippingProps {
  request: ServiceRequestData | undefined;
  mutateNewMessage?: () => void;
}

const MessagesShipping = ({
  request,
  mutateNewMessage,
}: MessagesShippingProps) => {
  const { t } = useTranslation('service-requests');
  const { isRoot, userLogged } = useContext(SessionContext);

  const [messageType, setMessageType] = useState<MessagesTypes>('ALL');
  const [message, setMessage] = useState<string>();

  const enableAttachment =
    request?.subStatus?.name === 'INTERROMPIDO' ||
    (request?.treatedStatus?.value && request?.type1?.name === 'RECLAMACAO');

  const restrictionTypes = useMemo(
    () => ({
      ALL: { id: 1, name: 'VISIVEL PARA TODOS' },
      ADMIN: { id: 4, name: 'VISIVEL PARA ADMINISTRADOR' },
    }),
    [],
  );

  const sendMessage = useCallback(async () => {
    await api.post(
      'service-proxy/service/request/executeActionServiceRequest',
      {
        serviceRequestId: request?.id,
        actionName: 'ANSWER',
        user: userLogged?.preferred_username,
        worklogs: [
          {
            note: message,
            restrictionType: messageType
              ? restrictionTypes?.[messageType]
              : restrictionTypes?.ALL,
          },
        ],
      },
      { params: { assetId: request?.asset?.assetNumber } },
    );

    mutateNewMessage?.();
    setMessage(undefined);
  }, [
    message,
    messageType,
    mutateNewMessage,
    request?.asset?.assetNumber,
    request?.id,
    restrictionTypes,
    userLogged?.preferred_username,
  ]);

  const handleTextAreaKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  return (
    <S.MessagesShippingContainer>
      {isRoot && (
        <Radio.Group
          defaultValue={messageType}
          onChange={({ target: { value } }) => setMessageType(value)}
        >
          {Object.keys(restrictionTypes)?.map(type => (
            <Radio.Button key={type} value={type}>
              {type === 'ALL' ? <UnlockOutlined /> : <LockOutlined />}
              {t(`restrictionTypes.${type}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}
      <S.MessagesShippingContent>
        <TextArea
          allowClear
          onPressEnter={handleTextAreaKeyDown}
          value={message}
          onChange={({ target: { value } }) => setMessage(value)}
          autoSize={{ minRows: 2, maxRows: 2 }}
        />

        {enableAttachment && !message ? (
          <AttachmentSending
            request={request}
            onMutateAttachment={mutateNewMessage}
          />
        ) : (
          <Button
            type="primary"
            size="large"
            icon={<SendOutlined />}
            onClick={sendMessage}
            disabled={!message}
          />
        )}
      </S.MessagesShippingContent>
    </S.MessagesShippingContainer>
  );
};

export default MessagesShipping;
